import React, { useState, useEffect } from 'react';
import { X, RefreshCw, Users, Mail, Phone, Instagram, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'arcana2026';
const ADMIN_DELETE_ENDPOINT = process.env.REACT_APP_ADMIN_DELETE_ENDPOINT || '/api/delete-submission.php';
const EXPORT_COLUMNS = ['Name', 'Email', 'Phone', 'Instagram', 'Submitted'];

const AdminPage = ({ onClose }) => {
  const [authed, setAuthed] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setAuthed(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized. Restart the dev server after updating .env.local.');
      }

      const { data: rows, error: supabaseError } = await supabase
        .from('submissions')
        .select('id, name, email, phone, instagram, submitted_at')
        .order('submitted_at', { ascending: false });

      if (supabaseError) {
        throw supabaseError;
      }

      const data = (rows || []).map((submission) => ({
        id: submission.id,
        name: submission.name,
        email: submission.email,
        phone: submission.phone,
        instagram: submission.instagram,
        submittedAt: submission.submitted_at,
      }));

      setSubmissions(data);
    } catch (err) {
      setError(err?.message || 'Failed to load submissions.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authed) return undefined;

    fetchSubmissions();

    const intervalId = window.setInterval(() => {
      fetchSubmissions();
    }, 8000);

    const handleFocus = () => {
      fetchSubmissions();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [authed]);

  const handleDeleteSubmission = async (submissionId) => {
    const confirmed = window.confirm('Delete this submission? This cannot be undone.');
    if (!confirmed) return;

    setDeletingId(submissionId);
    setError(null);

    try {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      if (isLocalhost) {
        if (!supabase) {
          throw new Error('Supabase client is not initialized. Restart the dev server after updating .env.local.');
        }

        const { error: deleteError } = await supabase
          .from('submissions')
          .delete()
          .eq('id', submissionId);

        if (deleteError) {
          throw deleteError;
        }
      } else {
        const response = await fetch(ADMIN_DELETE_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            submissionId,
          }),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(result?.error || 'Failed to delete submission.');
        }
      }

      setSubmissions((current) => current.filter((submission) => submission.id !== submissionId));
    } catch (err) {
      setError(err?.message || 'Failed to delete submission.');
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getExportRows = () => {
    return submissions.map((sub) => ({
      Name: sub.name || '',
      Email: sub.email || '',
      Phone: sub.phone || '',
      Instagram: sub.instagram ? `@${String(sub.instagram).replace(/^@/, '')}` : '',
      Submitted: formatDate(sub.submittedAt),
    }));
  };

  const escapeCsvValue = (value) => {
    const stringValue = String(value ?? '');
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = () => {
    const rows = getExportRows();
    const header = EXPORT_COLUMNS.map(escapeCsvValue).join(',');
    const body = rows
      .map((row) => EXPORT_COLUMNS.map((column) => escapeCsvValue(row[column])).join(','))
      .join('\n');
    const csvContent = [header, body].filter(Boolean).join('\n');
    downloadFile(csvContent, 'arcana-submissions.csv', 'text/csv;charset=utf-8;');
  };

  const handleExportExcel = () => {
    const rows = getExportRows();
    const tableRows = rows
      .map((row) => `
        <tr>${EXPORT_COLUMNS.map((column) => `<td>${String(row[column] ?? '')}</td>`).join('')}</tr>
      `)
      .join('');

    const htmlContent = `
      <html>
        <head>
          <meta charset="UTF-8" />
        </head>
        <body>
          <table>
            <thead>
              <tr>${EXPORT_COLUMNS.map((column) => `<th>${column}</th>`).join('')}</tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `;

    downloadFile(htmlContent, 'arcana-submissions.xls', 'application/vnd.ms-excel;charset=utf-8;');
  };

  return (
    <div className="admin-overlay">
      <div className="admin-container">
        <div className="admin-header">
          <h1 className="admin-title">ARCANA · SUBMISSIONS</h1>
          <button className="admin-close" onClick={onClose}><X size={18} /></button>
        </div>

        {!authed ? (
          <form onSubmit={handleLogin} className="admin-login">
            <p className="admin-login-label">Enter admin password</p>
            <input
              type="password"
              value={passwordInput}
              onChange={e => { setPasswordInput(e.target.value); setPasswordError(false); }}
              className={`admin-input ${passwordError ? 'admin-input-error' : ''}`}
              placeholder="Password"
              autoFocus
            />
            {passwordError && <p className="admin-error-text">Incorrect password</p>}
            <button type="submit" className="admin-btn">Enter</button>
          </form>
        ) : (
          <div className="admin-body">
            <div className="admin-toolbar">
              <span className="admin-count">
                <Users size={14} /> {submissions.length} applicant{submissions.length !== 1 ? 's' : ''}
              </span>
              <div className="admin-actions">
                <button className="admin-export" onClick={handleExportCsv} disabled={loading || submissions.length === 0}>
                  Export CSV
                </button>
                <button className="admin-export" onClick={handleExportExcel} disabled={loading || submissions.length === 0}>
                  Export Excel
                </button>
                <button className="admin-refresh" onClick={fetchSubmissions} disabled={loading}>
                  <RefreshCw size={14} className={loading ? 'spin' : ''} />
                  {loading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
            </div>

            {error && <p className="admin-error-text">{error}</p>}

            {!loading && submissions.length === 0 && !error && (
              <p className="admin-empty">No submissions yet.</p>
            )}

            {submissions.length > 0 && (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th><span className="th-icon"><Users size={12} /></span> Name</th>
                      <th><span className="th-icon"><Mail size={12} /></span> Email</th>
                      <th><span className="th-icon"><Phone size={12} /></span> Phone</th>
                      <th><span className="th-icon"><Instagram size={12} /></span> Instagram</th>
                      <th>Submitted</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((sub, idx) => (
                      <tr key={sub.id}>
                        <td className="admin-td-num">{idx + 1}</td>
                        <td>{sub.name || '—'}</td>
                        <td>{sub.email || '—'}</td>
                        <td>{sub.phone || '—'}</td>
                        <td>{sub.instagram ? `@${sub.instagram.replace(/^@/, '')}` : '—'}</td>
                        <td className="admin-td-date">{formatDate(sub.submittedAt)}</td>
                        <td>
                          <button
                            type="button"
                            className="admin-delete"
                            onClick={() => handleDeleteSubmission(sub.id)}
                            disabled={deletingId === sub.id}
                          >
                            <Trash2 size={14} />
                            {deletingId === sub.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
